import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Trash2, Eye } from "lucide-react";
import { FilePicker } from "./shared/FilePicker";

function FileChip({ entryId, file, editable, onRemoved }) {
  const viewFile = async () => {
    try {
      const res = await client.get(`/document-register/${entryId}/file/${file.doc_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: file.content_type }));
      window.open(url, "_blank");
    } catch {
      toast.error("Could not open file");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${file.filename}"?`)) return;
    try {
      const res = await client.delete(`/document-register/${entryId}/file/${file.doc_id}`);
      toast.success("File removed");
      onRemoved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid={`document-file-${file.doc_id}`}>
      <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <span className="truncate text-xs text-slate-700 max-w-[200px]">{file.filename}</span>
      <Badge variant="outline" className="text-[10px] uppercase">{file.content_type === "application/pdf" ? "PDF" : "IMG"}</Badge>
      <button type="button" data-testid={`document-file-view-${file.doc_id}`} onClick={viewFile} className="text-slate-500 hover:text-amber-700">
        <Eye className="h-3.5 w-3.5" />
      </button>
      {editable && (
        <button type="button" data-testid={`document-file-remove-${file.doc_id}`} onClick={remove} className="text-slate-400 hover:text-red-600">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function DocumentEntryDialog({ entryId, onClose, onSaved }) {
  const isCreate = !entryId;
  const [entry, setEntry] = useState(null);
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entryId) return;
    client
      .get(`/document-register/${entryId}`)
      .then((res) => {
        setEntry(res.data);
        setDescription(res.data.description);
      })
      .catch((err) => {
        toast.error(getErrorMessage(err));
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return toast.error("Description is required");
    if (isCreate && files.length === 0) return toast.error("At least one file or photo is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("description", description.trim());
      files.forEach((f) => fd.append("files", f));
      if (isCreate) {
        await client.post("/document-register", fd);
        toast.success("Document entry created");
      } else {
        await client.put(`/document-register/${entryId}`, fd);
        toast.success("Document entry updated");
      }
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (!window.confirm("Deactivate this document entry? It will be hidden from the active register.")) return;
    setBusy(true);
    try {
      await client.post(`/document-register/${entryId}/deactivate`);
      toast.success("Entry deactivated");
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setBusy(false);
    }
  };

  const editable = !isCreate && entry?.editable;
  const canDeactivate = !isCreate && entry?.can_deactivate && entry?.active !== false;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="document-entry-dialog">
        <DialogHeader>
          <DialogTitle>{isCreate ? "Add Document Entry" : "Document Entry"}</DialogTitle>
        </DialogHeader>

        {!isCreate && !entry ? (
          <p className="py-8 text-center text-slate-400">Loading...</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {!isCreate && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                <span>
                  Site: <span className="font-medium text-slate-700">{entry.display_label}</span>
                </span>
                <span>Date: {new Date(entry.date).toLocaleDateString("en-IN")}</span>
                <span>By: {entry.entered_by_name || entry.entered_by}</span>
                {entry.active === false && <Badge className="bg-slate-400">Inactive</Badge>}
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Description *</p>
              <textarea
                data-testid="document-description-input"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-slate-50 disabled:text-slate-500"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!isCreate && !editable}
              />
            </div>

            {!isCreate && (
              <div>
                <p className="text-sm font-medium text-slate-700 mb-1.5">Files ({entry.files?.length || 0})</p>
                <div className="flex flex-wrap gap-2">
                  {(entry.files || []).map((f) => (
                    <FileChip key={f.doc_id} entryId={entryId} file={f} editable={editable} onRemoved={setEntry} />
                  ))}
                </div>
              </div>
            )}

            {(isCreate || editable) && (
              <FilePicker
                label={isCreate ? "Files / Photos" : "Add Files (optional)"}
                files={files}
                onChange={setFiles}
                testId="document-files-input"
                required={isCreate}
              />
            )}

            <div className="flex justify-between">
              <div>
                {canDeactivate && (
                  <Button type="button" variant="destructive" disabled={busy} data-testid="document-deactivate-button" onClick={deactivate}>
                    Deactivate Entry
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  {isCreate || editable ? "Cancel" : "Close"}
                </Button>
                {(isCreate || editable) && (
                  <Button type="submit" disabled={busy} data-testid="document-submit-button" className="bg-amber-600 hover:bg-amber-700">
                    {busy ? "Saving..." : isCreate ? "Create Entry" : "Save Changes"}
                  </Button>
                )}
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
